import { loadLabels, updateLabel, deleteLabel, updateCategory, deleteCategory } from '@/voc/api/labels.api';
import isEmpty from 'lodash/isEmpty';
import Vue from 'vue';
import { runAggregationOnce, SUB_PRODUCT_AREAS } from '@/utils/aggregation';
import LabelAssociatedItemCountsAggregation from '@/voc/aggregations/label-associated-item-counts/label-associated-item-counts.aggregation';

const { GENERAL } = SUB_PRODUCT_AREAS || {};

const state = {
    labelOptions: [],
    labelCounts: [],
    promises: {
        labelOptions: null,
    },
};

const mutations = {
    setLabels (state, { labels }) {
        state.labelOptions = labels;
    },
    setLabelCounts (state, { labelCounts }) {
        state.labelCounts = labelCounts;
    },
    setPromise (state, { promise, key }) {
        Vue.set(state.promises, key, promise);
    },
};

const actions = {
    loadLabels ({ commit, state }, { reFetch = false } = {}) {
        if (state.promises.labelOptions) {
            return state.promises.labelOptions;
        }

        if (!isEmpty(state.labelOptions) && !reFetch) {
            return Promise.resolve();
        }

        LabelAssociatedItemCountsAggregation.subProductArea = GENERAL;
        const labelPromise = loadLabels();
        const associatedItemCountPromise = runAggregationOnce(LabelAssociatedItemCountsAggregation);

        const promise = Promise.all([labelPromise, associatedItemCountPromise])
            .then(([labels, associatedItemCounts]) => {
                commit('setLabels', { labels });
                commit('setLabelCounts', { labelCounts: associatedItemCounts });
            })
            .catch((error) => {
                throw error;
            })
            .finally(() => {
                commit('setPromise', { key: 'labelOptions', promise: null });
            });

        commit('setPromise', { key: 'labelOptions', promise });

        return promise;
    },
    async updateLabel ({ commit, state }, { labelId, name, categoryId }) {
        try {
            const editedLabel = await updateLabel(labelId, name, categoryId);
            const labels = state.labelOptions.map((label) => {
                if (label.id === editedLabel.id) {
                    return {
                        ...editedLabel,
                        associatedItemCount: label.associatedItemCount,
                    };
                }

                return label;
            });
            commit('setLabels', { labels });

            return editedLabel;
        } catch (error) {
            throw error;
        }
    },
    async deleteLabel ({ commit, state }, { labelId }) {
        try {
            await deleteLabel(labelId);
            const labels = state.labelOptions.filter((label) => label.id !== labelId);

            commit('setLabels', { labels });

            return labelId;
        } catch (error) {
            throw error;
        }
    },
    async updateCategory ({ commit, state }, { categoryId, name }) {
        try {
            const editedCategory = await updateCategory(categoryId, name);
            const labels = state.labelOptions.map((label) => {
                if (label.category?.id === editedCategory.id) {
                    return {
                        ...label,
                        category: editedCategory,
                    };
                }

                return label;
            });
            commit('setLabels', { labels });

            return editedCategory;
        } catch (error) {
            throw error;
        }
    },
    async deleteCategory ({ commit, state }, { categoryId }) {
        try {
            await deleteCategory(categoryId);
            const labels = state.labelOptions.filter((label) => label.category?.id !== categoryId);

            commit('setLabels', { labels });

            return categoryId;
        } catch (error) {
            throw error;
        }
    },
};

const getters = {
    labels: (state) => state.labelOptions,
    ideaLabels: (state) => state.labelOptions.map((label) => ({
        ...label,
        associatedItemCount: state.labelCounts.find((itemCount) => itemCount.labelId === label.id && itemCount.type === 'idea')?.count || 0,
    })),
    feedbackLabels: (state) => state.labelOptions.map((label) => ({
        ...label,
        associatedItemCount: state.labelCounts.find((itemCount) => itemCount.labelId === label.id && itemCount.type === 'feedback')?.count || 0,
    })),
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
